import React, { useState, useEffect } from 'react';
import { FaStar, FaQuoteLeft } from 'react-icons/fa';
import '../assets/css/Testimonials.css';
import { API_BASE_URL } from '../config';

const Testimonials = () => {
  // Add fallback testimonials for immediate display
  const fallbackTestimonials = [
    {
      id: 1,
      text: "Design Craft provided exceptional office furniture that transformed our workspace. The quality and attention to detail exceeded our expectations.",
      author: "Ahmed Al-Rashid",
      position: "CEO, Emirates Business Solutions"
    },
    {
      id: 2,
      text: "Outstanding service and premium furniture quality. Our employees love the new ergonomic chairs and executive desks. Highly recommended!",
      author: "Sarah Johnson",
      position: "Operations Manager, Dubai Tech Hub"
    },
    {
      id: 3,
      text: "Professional team, excellent craftsmanship, and timely delivery. Design Craft made our office renovation project seamless and successful.",
      author: "Mohammed Hassan",
      position: "Facilities Director, Gulf Industries"
    }
  ];

  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Set fallback testimonials immediately
    setTestimonials(fallbackTestimonials);
    
    // Then try to fetch real testimonials
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}testimonials.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && Array.isArray(data) && data.length > 0) {
        const formattedTestimonials = data.map(item => ({
          id: item.id,
          text: item.message,
          author: item.person_name,
          position: item.designation
        }));
        setTestimonials(formattedTestimonials);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      // Keep using fallback testimonials on error
      setTestimonials(fallbackTestimonials);
    }
  };

  // Navigate to previous slide
  const prevSlide = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : testimonials.length - 1));
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // Navigate to next slide
  const nextSlide = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentIndex((prevIndex) => (prevIndex < testimonials.length - 1 ? prevIndex + 1 : 0));
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // Auto-advance slides (paused for now)
  /*
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currentIndex]);
  */

  // Render 5 stars for Google Reviews
  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => (
      <FaStar key={index} className="google-star" />
    ));
  };

  return (
    <div className="testimonials-section">
      <div className="testimonials-container">
        <h2 className="testimonials-title">Testimonial</h2>
        <p className="testimonials-subtitle">
          Discover what our customers are saying about us. Real stories, real experiences – 
          find out why they choose us.
        </p>
        
        {/* Google Reviews Section - Moved to top */}
        <div className="google-reviews-section">
          <div className="google-header">
            <div className="google-g-logo">
              <svg viewBox="0 0 48 48" className="g-icon">
                <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
            </div>
            <div className="google-text-container">
              <div className="google-title">Google</div>
              <div className="star-rating">
                {renderStars()}
              </div>
            </div>
          </div>
        </div>
        
        <div className="testimonials-slider">
          <button 
            className="slider-btn prev-btn" 
            onClick={prevSlide}
            aria-label="Previous testimonial"
          >
            <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          
          <div className="slider-content">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id}
                className={`testimonial-slide ${index === currentIndex ? 'active' : ''}`}
                style={{ 
                  position: 'relative',
                  display: index === currentIndex ? 'flex' : 'none',
                  opacity: index === currentIndex ? 1 : 0,
                  transition: 'opacity 0.5s ease'
                }}
              >
                <p className="testimonial-text">{testimonial.text}</p>
                <h3 className="testimonial-author">{testimonial.author}</h3>
                <p className="testimonial-position">{testimonial.position}</p>
              </div>
            ))}
          </div>
          
          <button 
            className="slider-btn next-btn" 
            onClick={nextSlide}
            aria-label="Next testimonial"
          >
            <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 6L15 12L9 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        
        <div className="testimonial-indicators">
          {testimonials.map((_, index) => (
            <div 
              key={index} 
              className={`indicator ${index === currentIndex ? 'active' : ''}`} 
              onClick={() => {}}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

      </div>
    </div>
  );
};

export default Testimonials; 